// src/components/resources/tools/generators/BuyerPersona/ImageBasedPdfExport.tsx

"use client"

import { useState, useEffect, type ReactElement, type FormEvent, type FC } from 'react'
import { useIsomorphicLayoutEffect } from '~/lib/hooks/useIsomorphicLayoutEffect'
import { DocumentMagnifyingGlassIcon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { useEmailForm } from '~/components/forms/base/useEmailForm'
import { usePersona } from '~/components/resources/tools/generators/BuyerPersona/PersonaContext'
import { safeString } from '~/lib/utils/formatters'
import { cn } from '~/lib/utils'
import { siteConfig } from '~/lib/config/site'
import { captureToCanvas, warmExportPipeline, type ExportEngine } from '~/components/resources/tools/generators/shared/exportImage'
import { sendResourceEvent } from '~/lib/gtag'
import { clientLogger, logClientError } from '~/lib/logger-client'

/**
 * Custom hook that determines if the user prefers dark mode
 * based on the app's current theme setting
 * 
 * @returns {boolean} Whether dark mode is currently active
 */
function useIsDarkMode(): boolean {
  const [isDarkMode, setIsDarkMode] = useState(false)
  
  useEffect(() => {
    // Check for app-specific theme setting
    const appIsDarkMode = document.documentElement.classList.contains('dark')
    
    // Set dark mode based on current app setting
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reflects client-only runtime state on mount (unavailable during SSR)
    setIsDarkMode(appIsDarkMode)
    
    // Create an observer to watch for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const newIsDarkMode = document.documentElement.classList.contains('dark')
          setIsDarkMode(newIsDarkMode)
        }
      })
    })
    
    // Start observing document element for class changes
    observer.observe(document.documentElement, { attributes: true })
    
    // Clean up observer on unmount
    return () => observer.disconnect()
  }, [])
  
  return isDarkMode
}

/**
 * Fixes badge text centering issues with html2canvas by forcibly rewrapping content
 * This addresses the flexbox rendering problems in the PDF export
 * 
 * @param {HTMLElement} element - The element containing badges to fix
 */
function fixBadgeTextCentering(element: HTMLElement): void {
  // Get all badges using our specific selectors that match our app's badge patterns
  const badges = element.querySelectorAll('.badge, .list-badge')
  
  badges.forEach(badge => {
    if (badge instanceof HTMLElement) {
      // Skip large containers that aren't actual badges
      if (badge.childElementCount > 5 || badge.offsetWidth > 200) return
      
      // Skip elements that are already fixed or are containers for other badges
      if (badge.classList.contains('fixed-badge') || badge.querySelector('.badge')) return
      
      // Mark as fixed to prevent double-processing
      badge.classList.add('fixed-badge')
      
      // Store original content
      const originalContent = badge.innerHTML
      badge.innerHTML = ''
      
      // Create a wrapper span with forced centering
      const wrapper = document.createElement('span')
      wrapper.style.cssText = `
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
        height: 100% !important;
        text-align: center !important;
        margin-bottom: 10px !important;
      `
      
      // Put the original content back inside this wrapper
      wrapper.innerHTML = originalContent
      
      // Add wrapper to badge
      badge.appendChild(wrapper)
      
      // Apply strong styling to badge itself
      badge.style.cssText += `
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        text-align: center !important;
        padding: 4px 10px !important;
        min-height: 24px !important;
        overflow: visible !important;
      `
    }
  })
}

/**
 * Properly handles page break avoidance for elements with avoid-break class
 * Calculates sizes and adds appropriate spacing to prevent breaking across pages
 * 
 * @param {HTMLElement} element - The element containing sections to process
 * @param {number} pdfWidth - The width of the PDF in points
 * @param {number} pdfHeight - The height of the PDF in points
 */
function handlePageBreakAvoidance(element: HTMLElement, _pdfWidth: number, pdfHeight: number): void {
  const avoidBreakElements = element.querySelectorAll('.avoid-break')
  const pageHeightInPx = pdfHeight * 2 // Convert from PDF units to approximate screen pixels
  
  // Calculate approximate page boundaries for better break decisions
  const pageBreakPoints: number[] = []
  for (let i = 1; i < 10; i++) { // Support up to 10 pages
    pageBreakPoints.push(i * pageHeightInPx)
  }
  
  avoidBreakElements.forEach((section) => {
    if (section instanceof HTMLElement) {
      // Get element dimensions and position
      const rect = section.getBoundingClientRect()
      const elementHeight = rect.height
      
      // Apply strong page break avoidance styles
      section.style.pageBreakInside = 'avoid'
      section.style.breakInside = 'avoid'
      section.style.display = 'block'
      
      // Add significant margins to help force natural page breaks
      section.style.marginBottom = '3rem'
      section.style.paddingBottom = '1rem'
      
      // If element is large (more than 70% of page height), don't try to keep it together
      if (elementHeight > pageHeightInPx * 0.7) {
        // For very large elements, allow breaks but add some structure
        section.style.pageBreakInside = 'auto'
        section.style.breakInside = 'auto'
        
        // Add internal padding to make breaks look more intentional
        section.style.padding = '1rem 0'
      } else {
        // For smaller elements that should stay together, add extra spacing
        // to push them to the next page if they're near a page boundary
        const sectionTop = section.offsetTop
        const sectionBottom = sectionTop + elementHeight
        
        // Check if element is close to a page boundary
        for (const breakPoint of pageBreakPoints) {
          // If element would cross a page boundary
          if (sectionTop < breakPoint && sectionBottom > breakPoint) {
            // If element is closer to the next page, push it there with margin
            if (breakPoint - sectionTop < sectionBottom - breakPoint) {
              const previousElement = section.previousElementSibling
              if (previousElement instanceof HTMLElement) {
                previousElement.style.marginBottom = '4rem'
              }
            } else {
              // If element is closer to the previous page, add margin to previous element
              section.style.marginTop = '4rem'
            }
            break
          }
        }
      }
    }
  })
}

/**
 * Applies targeted fixes to specific problematic elements identified in the UI
 * These fixes address issues with badges, SVG icons, and section spacing
 * 
 * @param {HTMLElement} element - The element to apply fixes to
 * @param {number} pdfWidth - The width of the PDF in points
 * @param {number} pdfHeight - The height of the PDF in points
 * @param {boolean} isDarkMode - Whether to apply dark mode styling
 */
function applyTargetedStyleFixes(
  element: HTMLElement, 
  pdfWidth: number, 
  pdfHeight: number,
  isDarkMode: boolean
): void {
  // Apply the badge text centering fix first
  fixBadgeTextCentering(element)
  
  // Apply page break avoidance handling
  handlePageBreakAvoidance(element, pdfWidth, pdfHeight)
  
  // 1. FIX: Target each specific badge pattern in PersonaOutput.tsx
  
  // A. Communication channel badges with mr-1.5 icon containers
  const channelBadges = element.querySelectorAll('.badge.list-badge.inline-flex')
  channelBadges.forEach(badge => {
    if (badge instanceof HTMLElement) {
      // Make badge a proper flex container
      badge.style.display = 'inline-flex'
      badge.style.alignItems = 'center'
      badge.style.justifyContent = 'flex-start'
      badge.style.whiteSpace = 'nowrap'
      badge.style.verticalAlign = 'middle'
      
      // Add margin to text content
      const textNodes = Array.from(badge.childNodes).filter(node => 
        node.nodeType === Node.TEXT_NODE || 
        (node instanceof HTMLElement && !node.querySelector('svg'))
      )
      
      textNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
          const span = document.createElement('span')
          span.textContent = node.textContent
          span.style.marginBottom = '10px'
          node.parentNode?.replaceChild(span, node)
        } else if (node instanceof HTMLElement && !node.querySelector('svg')) {
          node.style.marginBottom = '10px'
        }
      })
      
      // Fix the icon container with mr-1.5 class
      const iconContainer = badge.querySelector('.mr-1\\.5')
      if (iconContainer instanceof HTMLElement) {
        iconContainer.style.display = 'inline-flex'
        iconContainer.style.alignItems = 'center'
        iconContainer.style.justifyContent = 'center'
        iconContainer.style.marginRight = '0.375rem' // Ensure proper spacing
        iconContainer.style.lineHeight = '1'
        iconContainer.style.marginTop = '8px' // Add 8px top margin to SVG container
        
        // Fix SVG within icon container
        const svg = iconContainer.querySelector('svg')
        if (svg instanceof SVGElement) {
          svg.style.display = 'block'
          svg.style.width = '100%'
          svg.style.height = '100%'
          svg.style.margin = '0'
        }
      }
    }
  })
  
  // B. Standard badges without icons
  const standardBadges = element.querySelectorAll('.badge:not(.inline-flex)')
  standardBadges.forEach(badge => {
    if (badge instanceof HTMLElement) {
      badge.style.display = 'inline-block'
      badge.style.textAlign = 'center'
      badge.style.verticalAlign = 'middle'
      badge.style.lineHeight = '1.5'
      
      // Add margin to text content
      const textContent = badge.textContent
      if (textContent?.trim()) {
        badge.innerHTML = `<span style="display: inline-block; margin-bottom: 10px;">${textContent}</span>`
      }
    }
  })
  
  // C. Demographic item icon containers (circular)
  const demoIcons = element.querySelectorAll('.demographic-item .p-2.rounded-full')
  demoIcons.forEach(container => {
    if (container instanceof HTMLElement) {
      container.style.display = 'flex'
      container.style.alignItems = 'center'
      container.style.justifyContent = 'center'
      container.style.overflow = 'hidden'
      container.style.flexShrink = '0'
      container.style.borderRadius = '9999px'
      
      // Fix SVG inside circular container
      const svg = container.querySelector('svg')
      if (svg instanceof SVGElement) {
        svg.style.margin = '0'
        svg.style.padding = '0'
        svg.style.width = '24px'
        svg.style.height = '24px'
        svg.style.display = 'block'
      }
    }
  })
  
  // D. Fix psychographic items with icon + text
  const psychographicItems = element.querySelectorAll('.psychographic-item .flex.items-center')
  psychographicItems.forEach(container => {
    if (container instanceof HTMLElement) {
      container.style.display = 'flex'
      container.style.alignItems = 'center'
      container.style.gap = '0.5rem'
      
      // Circular icon containers in psychographic items
      const iconContainer = container.querySelector('.p-2.rounded-full')
      if (iconContainer instanceof HTMLElement) {
        iconContainer.style.display = 'flex'
        iconContainer.style.alignItems = 'center'
        iconContainer.style.justifyContent = 'center'
        iconContainer.style.flexShrink = '0'
        iconContainer.style.borderRadius = '9999px'
        
        // Fix SVG inside
        const svg = iconContainer.querySelector('svg')
        if (svg instanceof SVGElement) {
          svg.style.margin = '0'
          svg.style.width = '24px'
          svg.style.height = '24px'
        }
      }
      
      // Add margin to text content
      const textElement = container.querySelector('span, p, div:not(.p-2)')
      if (textElement instanceof HTMLElement) {
        textElement.style.marginBottom = '10px'
      }
    }
  })
  
  // E. Fix text badges with flex layout - reduce spacing between badges
  const badgeContainers = element.querySelectorAll('.flex.flex-wrap.gap-2')
  badgeContainers.forEach(container => {
    if (container instanceof HTMLElement) {
      // Reduce gap between badges
      container.style.gap = '0.25rem'
    }
  })
  
  const badgesWithFlex = element.querySelectorAll('.flex.flex-wrap.gap-2 .badge')
  badgesWithFlex.forEach(badge => {
    if (badge instanceof HTMLElement) {
      badge.style.display = 'inline-flex'
      badge.style.alignItems = 'center'
      badge.style.height = 'auto'
      badge.style.minHeight = '24px'
      badge.style.padding = '0.25rem 0.75rem'
      badge.style.margin = '0.125rem' // Reduce margin between badges
      badge.style.lineHeight = '1.5'
      
      // Wrap text content with margin
      const textContent = badge.textContent
      if (textContent?.trim() && !badge.querySelector('span')) {
        badge.innerHTML = `<span style="display: inline-block; margin-bottom: 10px;">${textContent}</span>`
      }
    }
  })
  
  // 2. Fix the persona header gradient text issue
  const personaHeaderName = element.querySelector('h1.persona-header-persona-name-text')
  if (personaHeaderName instanceof HTMLElement) {
    // Remove the background gradient and text transparency which causes rendering issues
    personaHeaderName.style.backgroundClip = 'unset'
    personaHeaderName.style.backgroundImage = 'none'
    personaHeaderName.style.webkitBackgroundClip = 'unset'
    personaHeaderName.style.webkitTextFillColor = 'unset'
    personaHeaderName.style.textTransform = 'none'
    
    // Set solid text color based on theme
    if (isDarkMode) {
      personaHeaderName.style.color = '#a5b4fc' // Indigo-300 for dark mode
    } else {
      personaHeaderName.style.color = '#4f46e5' // Indigo-600 for light mode
    }
  }
  
  // 3. Fix general alignment issues
  
  // Fix all SVGs within the persona to have consistent display
  const allSvgs = element.querySelectorAll('svg')
  allSvgs.forEach(svg => {
    if (svg instanceof SVGElement) {
      // Only fix SVGs that don't already have explicit styles
      if (!svg.style.display) {
        svg.style.display = 'block'
      }
      // Add minimum dimensions if not specified
      if (!svg.style.width) {
        svg.style.minWidth = '1em'
        svg.style.minHeight = '1em'
      }
    }
  })
  
  // 4. Ensure dark mode margins are correctly colored
  if (isDarkMode) {
    // Ensure top/bottom margins have dark background
    const pageContainer = element.parentElement
    if (pageContainer instanceof HTMLElement) {
      pageContainer.style.backgroundColor = '#111827'
    }
  }
  
  // 5. Improve page break handling for major sections
  // Find all major sections in the persona output
  const sections = element.querySelectorAll(
    'section, ' +
    '.persona-section, ' +
    'div[class*="-section"], ' +
    'h2'
  )
  
  sections.forEach(section => {
    if (section instanceof HTMLElement) {
      // Skip very small elements that are likely not actual sections
      if (section.offsetHeight < 50) return
      
      // Add avoid-break class for our handler to process
      if (!section.classList.contains('avoid-break')) {
        section.classList.add('avoid-break')
      }
      
      // Apply direct page break prevention
      section.style.pageBreakInside = 'avoid'
      section.style.breakInside = 'avoid'
      
      // For headings, ensure they stay with the content that follows
      if (section.tagName === 'H2') {
        section.style.pageBreakAfter = 'avoid'
        section.style.breakAfter = 'avoid'
      }
      
      // Check if this is a large section that might need to be allowed to break
      const rect = section.getBoundingClientRect()
      if (rect.height > pdfHeight * 0.7) {
        // For very large sections, allow breaks but add structure
        section.style.pageBreakInside = 'auto'
        section.style.breakInside = 'auto'
      }
    }
  })
  
  // 6. Add spacing between major sections to encourage natural page breaks
  const majorSections = element.querySelectorAll('section, .section, [class*="section-"]')
  majorSections.forEach((section) => {
    if (section instanceof HTMLElement) {
      section.style.marginBottom = '2.5rem'
      section.style.paddingBottom = '0.5rem'
    }
  })
}

/**
 * Generates a high-fidelity PDF blob from a DOM element using a direct screenshot approach
 * for more accurate visual representation
 * 
 * @param {HTMLElement} element - The DOM element to convert to PDF
 * @param {boolean} forceDarkMode - Optional override for dark mode setting
 * @returns the generated PDF blob plus which engine produced the underlying
 *   capture (so the caller can record the cross-browser fallback rate), or null
 *   if generation failed.
 */
export interface HiFiPdfResult {
  blob: Blob
  engineUsed: ExportEngine
  usedFallback: boolean
}

export async function generateHiFiPdfBlob(element: HTMLElement, forceDarkMode?: boolean): Promise<HiFiPdfResult | null> {
  // Hoisted so the `finally` can always tear down the off-screen wrapper, even
  // when capture/jsPDF throws after it's been appended to `document.body` —
  // otherwise a PDF-generation failure leaks the (full clone) wrapper at
  // `left: -9999px` permanently.
  let wrapper: HTMLDivElement | null = null
  try {
    // jsPDF is the only library imported here now — the DOM capture goes through
    // the shared `captureToCanvas` (modern-screenshot by default, automatic
    // html2canvas fallback), the same engine the mockup generators use, so the
    // PDF gets the browser-faithful rasterization instead of html2canvas's CSS
    // reimplementation. (#341 follow-up — see docs/case-studies/export-engine-fidelity.md.)
    const { jsPDF } = await import('jspdf')
    
    // Create a clone to avoid modifying the original
    const elementToCapture = element.cloneNode(true) as HTMLElement
    
    // Determine dark mode from parameter or document state
    const usesDarkMode = forceDarkMode ?? document.documentElement.classList.contains('dark')
    
    // Set background color based on theme - using #111827 for dark mode
    const bgColor = usesDarkMode ? '#111827' : '#FFFFFF'
    
    // Create a full-height container with background color to ensure it extends to fill the page
    const fullHeightContainer = document.createElement('div')
    fullHeightContainer.style.backgroundColor = bgColor
    fullHeightContainer.style.minHeight = '1100px' // Taller than A4 page
    fullHeightContainer.style.width = '100%'
    fullHeightContainer.style.padding = '20px'
    fullHeightContainer.style.paddingBottom = '100px' // Extra padding at bottom
    
    // Add the element to capture inside this container
    fullHeightContainer.appendChild(elementToCapture)
    
    // Create a wrapper with appropriate background
    wrapper = document.createElement('div')
    wrapper.style.position = 'absolute'
    wrapper.style.left = '-9999px'
    wrapper.style.top = '0'
    wrapper.style.width = element.getBoundingClientRect().width + 'px'
    wrapper.style.overflow = 'visible'
    
    // Apply dark mode class if needed
    if (usesDarkMode) {
      wrapper.classList.add('dark')
      // Also add dark mode styles directly to ensure they're captured
      wrapper.style.backgroundColor = '#111827'
      wrapper.style.color = '#f3f4f6'
    } else {
      // Ensure light mode styles
      wrapper.style.backgroundColor = '#FFFFFF'
      wrapper.style.color = '#111827'
    }
    
    wrapper.appendChild(fullHeightContainer)
    document.body.appendChild(wrapper)
    
    // Wait for rendering
    await new Promise(resolve => setTimeout(resolve, 500))
    
    // Create PDF with proper dimensions
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: 'a4'
    })
    
    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = pdf.internal.pageSize.getHeight()
    
    // Apply our targeted fixes for badges, icons, and spacing
    // Pass PDF dimensions to help with page break calculations
    applyTargetedStyleFixes(elementToCapture, pdfWidth, pdfHeight, usesDarkMode)
    
    // Wait a bit more for style changes to apply
    await new Promise(resolve => setTimeout(resolve, 200))
    
    // Capture the prepared element through the shared engine. The theme bgColor
    // is baked in (opaque) so the slices below composite correctly; the html2canvas
    // fallback keeps the PDF export working if foreignObject capture fails.
    const captured = await captureToCanvas({
      element: fullHeightContainer,
      scale: 2,
      backgroundColor: bgColor,
    })
    if (!captured) {
      clientLogger.error('PDF capture produced an empty canvas', { component: 'ImageBasedPdfExport' })
      return null
    }
    const { canvas, engineUsed, usedFallback } = captured

    // Fill the entire PDF background with theme color first
    if (usesDarkMode) {
      pdf.setFillColor(17, 24, 39) // #111827 in RGB
    } else {
      pdf.setFillColor(255, 255, 255) // White in RGB
    }
    pdf.rect(0, 0, pdfWidth, pdfHeight, 'F') // 'F' means fill
    
    // Calculate page splitting
    const contentWidth = canvas.width
    const contentHeight = canvas.height
    const scale = pdfWidth / contentWidth
    const scaledHeight = contentHeight * scale
    
    // Check if content fits on one page
    if (scaledHeight <= pdfHeight - 40) {
      // All content fits on one page
      pdf.addImage(
        canvas.toDataURL('JPEG', 0.95),
        'JPEG',
        0, 20,
        pdfWidth,
        scaledHeight
      )
    } else {
      // Split content across multiple pages
      const pageContentHeight = pdfHeight
      const pageContentHeightInCanvas = pageContentHeight / scale  // Direct calculation of pixels per page
      let position = 0
      
      while (position < contentHeight) {
        // For pages after the first one, add a new page and fill with background color
        if (position > 0) {
          pdf.addPage()
          if (usesDarkMode) {
            pdf.setFillColor(17, 24, 39) // #111827 in RGB
          } else {
            pdf.setFillColor(255, 255, 255) // White in RGB
          }
          pdf.rect(0, 0, pdfWidth, pdfHeight, 'F')
        }
        
        // Calculate slice height
        const sliceHeight = Math.min(pageContentHeightInCanvas, contentHeight - position)
        
        // Create temporary canvas for this slice
        const tempCanvas = document.createElement('canvas')
        tempCanvas.width = contentWidth
        tempCanvas.height = sliceHeight
        
        const ctx = tempCanvas.getContext('2d')
        if (ctx) {
          // Fill with background color first
          ctx.fillStyle = bgColor
          ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height)
          
          // Draw slice
          ctx.drawImage(
            canvas,
            0, position,
            contentWidth, sliceHeight,
            0, 0,
            contentWidth, sliceHeight
          )
          
          // Add slice to PDF
          pdf.addImage(
            tempCanvas.toDataURL('JPEG', 0.95),
            'JPEG',
            0, 0,
            pdfWidth,
            (sliceHeight * scale)
          )
        }
        
        // Move to next slice
        position += sliceHeight
      }
    }
    
    return { blob: pdf.output('blob'), engineUsed, usedFallback }
  } catch (error) {
    logClientError(error, { component: 'ImageBasedPdfExport', context: 'Error generating PDF' })
    return null
  } finally {
    // Always remove the off-screen wrapper — on success, the empty-canvas
    // early return, and any thrown error alike.
    if (wrapper?.parentNode) {
      wrapper.parentNode.removeChild(wrapper)
    }
  }
}

/**
 * Email modal component for gating PDF downloads
 * Prompts user to enter their email before allowing download
 */
function EmailCaptureModal({ 
  isOpen, 
  onClose, 
  onSuccessfulSubmit 
}: { 
  isOpen: boolean 
  onClose: () => void 
  onSuccessfulSubmit: (email: string) => void 
}): ReactElement | null {
  const { formData } = usePersona()
  
  // Safely extract persona name with proper string conversion and fallback handling
  const personaNameRaw = formData.personaName ?? formData.name
  const personaName = safeString(personaNameRaw, { fallback: 'Buyer Persona' })
  
  // Configure email form
  const {
    email,
    error,
    isSubmitting,
    isSuccess,
    handleChange,
    handleSubmit,
    handleBlur
  } = useEmailForm({
    validation: {
      rules: {
        required: true,
        format: true,
        requireCorporateEmail: false
      },
      messages: {
        required: 'Please enter your email address',
        invalid: 'Please enter a valid email address'
      }
    },
    crm: {
      tags: ['buyer_persona_generator_download'],
      options: {
        addToList: true,
        retryOnFailure: true,
        retryAttempts: 2
      }
    },
    template: {
      templateAlias: 'gated-content-2',
      to: '', // Will be set by the form
      from: process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
      templateModel: {
        gatedContentNoDownload: {
          content_name: `${personaName} Buyer Persona`,
          email: '' // Will be set by the form
        }
      }
    },
    analytics: {
      enabled: true,
      category: 'buyer_persona',
      label: 'buyer_persona_download',
      pageLocation: 'buyer_persona_generator',
      elementId: 'email_gate_button'
    },
    onSuccess: () => {
      // Wait a moment to show success message before closing
      setTimeout(() => {
        onSuccessfulSubmit(email)
      }, 1500)
    }
  })

  // Handle form submission
  const handleFormSubmit = (e: FormEvent): void => {
    e.preventDefault()
    void handleSubmit(e)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity dark:bg-gray-900/75" onClick={onClose} />

      <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 text-left shadow-xl transition-all dark:bg-gray-900">
        <div className="absolute right-4 top-4">
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            disabled={isSubmitting}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col items-center">
          <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-900/30">
            <DocumentMagnifyingGlassIcon className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
          </div>

          <h3 className="mb-4 text-center text-xl font-semibold leading-tight text-gray-900 dark:text-white">
            {`Download Your ${personaName} Persona`}
          </h3>

          <p className="mb-6 text-center text-sm leading-relaxed text-gray-600 dark:text-gray-400">
            Enter your email to download your custom buyer persona PDF.
          </p>

          <form onSubmit={handleFormSubmit} className="w-full">
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={cn(
                  "w-full rounded-xl border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-gray-200 placeholder:text-gray-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:ring-gray-700 dark:placeholder:text-gray-500",
                  isSubmitting && "cursor-not-allowed opacity-50",
                  error && "ring-red-300 dark:ring-red-700 focus:ring-red-500"
                )}
                placeholder="Enter your email"
                required
                disabled={isSubmitting || isSuccess}
              />
            </div>

            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
              By submitting your email, you agree to receive marketing communications from us. You can unsubscribe at any time.
            </p>

            {error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
            {isSuccess && (
              <p className="mt-2 text-sm text-emerald-600 dark:text-emerald-400">
                Success! Your PDF is being prepared...
              </p>
            )}

            <div className="mt-6 flex flex-col gap-3">
              <button
                type="submit"
                disabled={isSubmitting || isSuccess}
                className={cn(
                  "w-full rounded-xl bg-indigo-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:hover:bg-indigo-500",
                  "flex items-center justify-center gap-2",
                  (isSubmitting || isSuccess) && "cursor-not-allowed opacity-50"
                )}
              >
                {isSubmitting ? 'Processing...' : 'Download PDF'}
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
              
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="w-full rounded-xl px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
            </div>
          </form>

          <p className="mt-6 text-xs text-center text-gray-500 dark:text-gray-400">
            By continuing, you agree to our{' '}
            <a href={`${siteConfig.baseLinks.privacy}`} className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href={`${siteConfig.baseLinks.terms}`} className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
              Terms of Service
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Component for PDF export with layout fixes
 * Provides a button that generates a high-fidelity PDF of the persona
 * Now gated behind an email signup form
 */
export const ImageBasedPdfExport: FC = () => {
  const { formData } = usePersona()
  const [isGenerating, setIsGenerating] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  // Cache Components: close the email-capture modal when this route is hidden by
  // <Activity>, so its post-submit success state isn't preserved open and shown
  // stale when the user navigates back to the generator.
  useIsomorphicLayoutEffect(() => {
    return () => setShowEmailModal(false)
  }, [])
  const isDarkMode = useIsDarkMode()

  // Warm the capture chunk(s) at idle so the first (gated) PDF export doesn't pay
  // the cold modern-screenshot + html2canvas-fallback dynamic import on the click
  // path — mirrors the mockup generators' `useExport` warm-on-mount.
  useEffect(() => warmExportPipeline(), [])

  /**
   * Generate and download the PDF
   */
  const generateHighFidelityPdf = async (): Promise<void> => {
    setIsGenerating(true)

    try {
      // Find the persona output container with multiple possible class names
      const element = document.querySelector('.persona-output') ?? 
                     document.querySelector('.pdf-ready-container') ??
                     document.querySelector('.persona-output-container') ??
                     document.querySelector('.persona-header')
                     
      if (!element) {
        clientLogger.error('Persona output element not found. Make sure your container has either "persona-output" or "pdf-ready-container" class.', { component: 'ImageBasedPdfExport' })
        alert('Could not find the persona content to export. Please try again or contact support.')
        setIsGenerating(false)
        return
      }
      
      // Generate the PDF with proper type safety and pass current theme
      const result = await generateHiFiPdfBlob(element as HTMLElement, isDarkMode)

      if (!result) {
        throw new Error('Failed to generate PDF')
      }

      // Create filename with better fallbacks and proper string conversion using safeString
      const personaNameRaw = formData.personaName ?? formData.name
      const personaNameStr = safeString(personaNameRaw, { fallback: 'buyer-persona' })

      const filename = `${personaNameStr}`
        .replace(/\s+/g, '-')
        .toLowerCase() + '-persona.pdf'

      // Download the PDF
      const url = URL.createObjectURL(result.blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      // Record which engine produced the capture + whether the html2canvas
      // fallback fired, so the cross-browser fallback rate is observable in prod
      // for the PDF path too (parity with the mockup downloads, #360).
      sendResourceEvent('complete', {
        resourceType: 'tool',
        resourceSlug: 'buyer-persona-generator',
        params: {
          tool_type: 'generator',
          export_format: 'pdf',
          export_engine: result.engineUsed,
          export_used_fallback: result.usedFallback,
          outcome: 'success',
        },
      })
    } catch (error) {
      logClientError(error, { component: 'ImageBasedPdfExport', context: 'Error generating PDF' })
      alert('There was an error generating your PDF. Please try again.')
      sendResourceEvent('complete', {
        resourceType: 'tool',
        resourceSlug: 'buyer-persona-generator',
        params: { tool_type: 'generator', export_format: 'pdf', outcome: 'error', error_type: 'export_failed' },
      })
    } finally {
      setIsGenerating(false)
    }
  }

  /**
   * Handle button click to show email modal instead of generating PDF directly
   */
  const handleButtonClick = (): void => {
    setShowEmailModal(true)
  }

  /**
   * Handle successful email submission
   */
  const handleSuccessfulEmailSubmit = (email: string): void => {
    // Close the modal
    setShowEmailModal(false)
    
    // Generate and download the PDF
    void generateHighFidelityPdf()
    
    // Could store the email in localStorage to avoid showing the modal again
    try {
      localStorage.setItem('buyerPersonaEmail', email)
    } catch (_error) {
      // Ignore localStorage errors - using underscore prefix to indicate intentionally unused
    }
  }

  return (
    <>
      <button
        onClick={handleButtonClick}
        disabled={isGenerating}
        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        title="Download High-Fidelity PDF"
      >
        <DocumentMagnifyingGlassIcon className="h-4 w-4 mr-2" />
        {isGenerating ? 'Generating...' : 'Download PDF'}
      </button>
      
      <EmailCaptureModal 
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSuccessfulSubmit={handleSuccessfulEmailSubmit}
      />
    </>
  )
}

export default ImageBasedPdfExport